/** Props for {@link CardPlaceholder}. */
export interface CardPlaceholderProps {
  /**
   * Short label rendered in monospace 8px at the bottom of the placeholder
   * (e.g. a card name or SKU). Text overflows with ellipsis if too long.
   */
  label?: string;
  /** Width in pixels. Default 56. */
  w?: number;
  /** Height in pixels. Default 78. */
  h?: number;
}

/**
 * CardPlaceholder — striped 45-degree gradient placeholder for missing card
 * images in inventory grids and binder slots.
 *
 * Shell: any.
 *
 * Reads the design system's `--surface-inset` / `--surface-2` roles for the
 * stripe, `--radius-sm` for the corner (the design system's "card slots, inputs,
 * chips" radius, which is what a card slot is) and Mantine's bridged
 * `--mantine-color-default-border` / `--mantine-color-dimmed` /
 * `--mantine-font-family-monospace`, so it adapts to the active colour scheme.
 * `flexShrink: 0` prevents the element from collapsing inside flex parents.
 *
 * Corrected 2026-08-04: this used to cite "`primitives.jsx` `CardPlaceholder`
 * lines 204–229" — one of the ghost sources
 * `mtb-design-system/platforms/alignment.md` §6 names, a design bundle in no
 * repo. It also striped itself with `--mantine-color-gray-1/-2`, which is
 * **stock Mantine gray**: the generated theme registers the MTB neutral ramp
 * under the key `ink` and overrides `dark`, but never overrides `gray`, so
 * anything reading `gray-*` renders off-brand neutrals. The stripe now reads
 * semantic surface roles instead (#107).
 */
export function CardPlaceholder({ label, w = 56, h = 78 }: CardPlaceholderProps) {
  return (
    <div
      data-testid="card-placeholder"
      title={label}
      style={{
        width: w,
        height: h,
        borderRadius: 'var(--radius-sm)',
        background:
          'repeating-linear-gradient(45deg, var(--surface-2) 0 6px, var(--surface-inset) 6px 12px)',
        border: '1px solid var(--mantine-color-default-border)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 'var(--space-1)',
        color: 'var(--mantine-color-dimmed)',
        fontFamily: 'var(--mantine-font-family-monospace)',
        // Deliberately off-scale and left literal. The design system's type
        // scale bottoms out at `--text-xs` (11px); at 11px this label
        // ellipsises to nothing inside a 56px-wide slot. Raised as a
        // design-system gap rather than papered over with the wrong token.
        fontSize: 8,
        textAlign: 'center',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {label != null && (
        <span
          style={{
            display: 'block',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}
